import { Button } from '@kcftech/design-system';
import {
    CurrentUserUpdateRequest,
    ICurrentUserResponse,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import TermsOfUse from 'assets/TermsOfUse.pdf';
import { DocumentTitle } from 'components/DocumentTitle';
import { sdClient } from 'contexts/SDKClientContext';
import { logout } from 'pages/Login/Login';
import { LoginScreen } from 'pages/Login/LoginScreen';
import { ExternalLink } from 'routing/ExternalLink';
import styled from 'styled-components';

const TermsContainer = styled.div`
    max-height: 50vh;
    overflow-y: auto;
    padding-right: 2rem;
    margin-bottom: 3rem;

    p {
        margin: 0;
        padding: 0;
    }

    p + p {
        margin-top: 1.5rem;
    }

    ul {
        li + li {
            margin-top: 1.5rem;
        }
    }
`;

const Buttons = styled.div`
    margin: 2rem 0;
    display: flex;
    justify-content: flex-end;
    gap: 2rem;
`;

export type EulaScreenProps = Readonly<{
    user: ICurrentUserResponse;
    onAccept: () => void;
}>;

export function EulaScreen({ user, onAccept }: EulaScreenProps) {
    const { mutate: acceptEula, isPending: isAcceptingEula } = useMutation({
        mutationFn: () => {
            const { FirstName, LastName, PhoneNumber } = user;

            return sdClient.updateCurrentUser({
                body: new CurrentUserUpdateRequest({
                    FirstName,
                    LastName,
                    PhoneNumber,
                    AcceptEula: true,
                }),
            });
        },

        onSuccess: onAccept,
    });

    return (
        <>
            <DocumentTitle title="SMARTdiagnostics EULA" />
            <LoginScreen>
                <TermsContainer>
                    <h2>STANDARD SOFTWARE END USER LICENSE AGREEMENT</h2>
                    <p>Effective Date: April 1, 2023</p>
                    <h3>SUMMARY</h3>
                    <p>
                        This summary provides a rough outline of the terms and conditions
                        below it. This summary is provided solely for convenience. In the
                        event of any conflict or uncertainty between the summary and the
                        terms and conditions, the terms and conditions shall control.
                    </p>
                    <ul>
                        <li>
                            This agreement is between KCF and your business for your
                            business&apos; use of all software and related products
                            provided by KCF. When we refer to “you,” we mean you and your
                            business, as applicable. KCF may modify this agreement and
                            will notify you if substantial changes are made.
                        </li>
                        <li>
                            KCF does not own or control your content (i.e., your data and
                            information), but KCF may copy, modify, and use your content
                            to provide and enhance the Software products and services.
                        </li>
                        <li>
                            KCF will take measures to safeguard your content against loss
                            and unauthorized access or disclosure. KCF will only use
                            commercially reasonable efforts to store the content for no
                            more than 1 year.
                        </li>
                        <li>
                            You are responsible for properly configuring and using all
                            Software products and services provided by KCF and maintaining
                            appropriate security, privacy, and backup of your content.
                        </li>
                        <li>
                            You agree that KCF&apos;s use of your content does not violate
                            the rights of a third party (e.g., another business) and that
                            KCF may remove your content from Software if it exposes KCF to
                            risk.
                        </li>
                        <li>
                            KCF solely owns all intellectual property associated with
                            Software, hardware, and future additions to Software.
                        </li>
                        <li>
                            KCF may maintain, upgrade, patch, and add feature enhancements
                            to Software. KCF will notify you in advance of making
                            substantial changes.
                        </li>
                        <li>
                            KCF is not liable for failure of your equipment, loss of your
                            content, inaccuracies in your content or conclusions or
                            actions derived from it, nor for the violation of the rights
                            of third parties (e.g., other businesses) by your content.
                        </li>
                        <li>
                            You agree to comply with domestic and international laws,
                            including laws pertaining to export of data.
                        </li>
                        <li>
                            Any legal action related to Software, or this agreement must
                            be brought in a court where KCF&apos;s main office is located.
                        </li>
                        <li>
                            Your Software license ends when this agreement is terminated
                            or expires based on the Terms of Sale. KCF may use your
                            content after the agreement is terminated or expires under the
                            same terms and conditions that apply to KCF&apos;s use of your
                            content while the agreement is in force.
                        </li>
                        <li>
                            By accepting this agreement you also accept KCF&apos;s Privacy
                            Policy (
                            <ExternalLink url="https://kcftech.com/privacy-policy/">
                                https://kcftech.com/privacy-policy/
                            </ExternalLink>
                            ) and Terms of Sale (
                            <ExternalLink url="https://kcftech.com/terms-of-sale/">
                                https://kcftech.com/terms-of-sale/
                            </ExternalLink>
                            ).
                        </li>
                    </ul>

                    <h3>TERMS AND CONDITIONS</h3>

                    <h4>1. Scope and Modification</h4>
                    <p>
                        These Software Terms of Use, including all applicable additional
                        terms and conditions, policies, and statements, such as the KCF
                        Privacy Policy and Terms of Sale, which are linked to and
                        incorporated by reference below (collectively, the “Agreement”),
                        set forth the terms and conditions governing access to and use of
                        Software products and services, including hardware, software, and
                        related services (“Software”), provided by KCF Technologies, Inc.,
                        336 South Fraser Street, State College, Pennsylvania (“KCF”). By
                        accessing or using such products or services on behalf or for the
                        benefit of a business entity or other organization, you accept
                        this Agreement as made between KCF and such business entity or
                        other organization for the benefit of its authorized account
                        users, including you (collectively, “You”). You shall be
                        responsible for all activity that takes place under Your accounts.
                        KCF may amend this Agreement from time to time by posting an
                        amended Agreement on our website (
                        <ExternalLink url="https://kcftech.com/terms-of-use/">
                            https://kcftech.com/terms-of-use/
                        </ExternalLink>
                        ) at least thirty (30) days before its effective date. KCF will
                        also notify You via email of any substantial amendments. Your
                        continued use of Software after the effective date of an amended
                        Agreement constitutes Your acceptance of the amended Agreement.
                    </p>

                    <h4>2. Content</h4>
                    <p>
                        Software collects and stores content, data, and information
                        (collectively, “Content”) entered by and acquired from You and
                        generates data related to such Content. KCF does not control Your
                        Content and does not claim ownership to Your Content. By providing
                        Content to Software, You grant to KCF the perpetual, irrevocable
                        right to copy, modify, and use Your Content and any data generated
                        from Your Content to provide Software to You and to develop and
                        enhance KCF&apos;s products and services, such as its analysis
                        algorithms, data manipulation techniques, and machine learning
                        methods. KCF will use commercially reasonable efforts to store
                        Your Content for no more than one (1) year from the date it was
                        originated and to provide You with the opportunity to download
                        Your Content prior to KCF&apos;s deleting it. You warrant that
                        KCF&apos;s use of Your Content will not infringe upon,
                        misappropriate, or violate rights of any third party and that KCF
                        may remove from Software any of Your Content that it believes
                        exposes KCF to risk, for any reason and in KCF&apos;s sole
                        discretion.
                    </p>
                    <p>
                        KCF will not disclose Your Content, in a way that identifies You,
                        to any third party, except as permitted by Section 3
                        (Confidentiality) and the KCF Privacy Policy. KCF may process Your
                        Content to generate anonymized data or data aggregated with other
                        KCF customers that does not identify You and use such anonymized
                        or aggregated data for any lawful purpose.
                    </p>

                    <h4>3. Confidentiality</h4>
                    <p>
                        KCF will not use or disclose Your Content and any information
                        provided by You that You identify in writing as confidential
                        except as needed to perform its obligations and exercise its
                        rights under this Agreement and in a manner consistent with the
                        KCF Privacy Policy. KCF may disclose Your confidential
                        information, or request that You disclose Your confidential
                        information, to KCF&apos;s subcontractors with a need to know the
                        confidential information in order for KCF to perform its
                        obligations and exercise its rights under this Agreement so long
                        as such subcontractors have entered into a non-disclosure and
                        non-use agreement with terms no less restrictive or protective of
                        Your confidential information than the terms of this Agreement.
                    </p>
                    <p>
                        Regardless of whether or not the following information is
                        designated as confidential information, it shall not be considered
                        confidential information: (a) information which is publicly known
                        as of the date of this Agreement; (b) information which becomes
                        publicly known, unless as a result of the fault of KCF; (c)
                        information which You agree in writing is not confidential; and
                        (d) information which is required to be produced by law or
                        judicial process (however, upon receipt of a demand for production
                        of confidential information pursuant to law or judicial process,
                        KCF shall use reasonable efforts to notify You of such demand in
                        sufficient time to allow You to move to prevent such disclosure).
                    </p>

                    <h4>4. Maintenance</h4>
                    <p>
                        In order to maintain the integrity and security of Software, KCF
                        may perform maintenance to Software, including upgrades, patches,
                        and feature enhancements on client-side devices (i.e., hardware on
                        Your premises that collects and provides Content to Software cloud
                        services) and server-side devices (i.e., hardware used by KCF to
                        receive Content from client-side devices and to provide Software
                        cloud services), at any time and within its discretion. KCF will
                        make commercially reasonable efforts to notify You in advance of
                        maintenance, except for emergency maintenance, and You agree to
                        make commercially reasonable efforts to enable KCF to perform all
                        maintenance under the circumstances.
                    </p>

                    <h4>5. Feedback and Intellectual Property</h4>
                    <p>
                        KCF is open to Your feedback, and You agree that KCF may use Your
                        feedback (including all ideas, comments, suggestions, techniques,
                        and concepts) to further enhance KCF&apos;s products and services
                        without any obligation to You, including any obligation of
                        compensation or attribution. Subject to the terms and conditions
                        of the Agreement, KCF grants to You a limited, non-exclusive,
                        non-transferable, non-sublicensable right to use Software for Your
                        own internal business purposes in a manner consistent with this
                        Agreement and any Software documentation provided by KCF. Except
                        as expressly provided in this Agreement or otherwise in writing,
                        KCF reserves all rights, title, and interest to Software and the
                        intellectual property related to Software developed by KCF,
                        including intellectual property developed by KCF that is based on
                        feedback or Content, unless otherwise limited by a separate
                        written agreement between You and KCF. Except as expressly
                        provided in this Agreement or otherwise in writing, You reserve
                        all rights, title, and interest to Your Content, Your equipment,
                        and Your business operations and processes independent of
                        Software, including all related intellectual property.
                    </p>

                    <h4>6. Warranties and Disclaimers</h4>
                    <p>
                        The Terms of Sale provide warranty and disclaimer of warranty
                        terms applicable to Software.
                    </p>

                    <h4>
                        7. Limitation of Liability, Indemnification, and Force Majeure
                    </h4>
                    <p>
                        To the maximum extent permitted by law, each of You and KCF agrees
                        that neither party, nor its affiliates, officers, directors,
                        employees, shareholders, representatives, agents, or contractors,
                        shall be liable to the other party, nor its affiliates, officers,
                        directors, employees, shareholders, representatives, agents, or
                        contractors, for any indirect, special, incidental, exemplary,
                        punitive, or consequential damages, nor for any damages arising
                        from delay, loss of goodwill, loss of, damage to, or unauthorized
                        access to data, interruption in use or availability of data, loss
                        of use of money or use of products, lost profits, revenue or
                        savings (actual or anticipated), or other economic loss ensuing
                        from or in connection with Software or this Agreement, even if
                        such party has been advised of the possibility of such damages.
                        KCF shall not be liable for inaccuracies in Your Content or
                        conclusions, or actions derived from it nor its violation of
                        third-party rights, nor for any failure of Your equipment not
                        provided by KCF. To the maximum extent permitted by law,
                        KCF&apos;s aggregate liability to You shall not exceed the monies
                        received from You for Your access to and use of Software. These
                        limitations shall apply notwithstanding the failure of the
                        essential purpose of any limited remedy.
                    </p>
                    <p>
                        KCF agrees to indemnify and hold You and Your affiliates harmless
                        from any damages arising out of or connected to claims, actions,
                        or proceedings asserted by third parties based upon or related to
                        (a) KCF&apos;s failure to properly license any third-party
                        Software provided by KCF to You in connection with providing
                        Software to You and (b) KCF&apos;s material breach of this
                        Agreement.
                    </p>
                    <p>
                        You agree to indemnify and hold KCF and affiliates harmless from
                        any damages arising out of or connected to claims, actions, or
                        proceedings asserted by third parties based upon or related to (a)
                        any unlicensed use of Software and (b) Your material breach of
                        this Agreement.
                    </p>
                    <p>
                        Neither You nor KCF will be liable for delay or failure to perform
                        obligations under this Agreement where the delay or failure
                        results from a cause beyond either party&apos;s reasonable
                        control, such as utility failures or, more generally, acts of God.
                    </p>
                    <p>
                        The Terms of Sale provide additional limitation of liability,
                        indemnification, and force majeure applicable to Software. To the
                        extent of any conflict or ambiguity between the terms in this
                        Section above and similar terms in the Terms of Sale, the terms in
                        this Section above shall control.
                    </p>

                    <h4>8. Compliance with Certain Laws</h4>
                    <p>
                        You agree to comply with domestic and international laws,
                        including export laws and regulations, that apply to Your use of
                        Software.
                    </p>

                    <h4>9. Expiration and Termination</h4>
                    <p>
                        Your license to use Software shall end upon expiration or
                        termination of this Agreement or as provided in the Terms of Sale.
                        KCF&apos;s license to use Your Content as provided above shall
                        survive any expiration or termination of this Agreement. After
                        expiration or termination of this Agreement, KCF may retain Your
                        Content indefinitely, but KCF is not obligated to retain or
                        maintain Your Content or Your access to Content, provided that KCF
                        will provide a reasonable means for You to retrieve Your Content
                        upon expiration or termination of this Agreement at Your request
                        received prior to such expiration or termination. KCF&apos;s
                        obligations of confidentiality under this Agreement with respect
                        to Your Content and Your confidential information shall survive
                        for as long as KCF retains Your Content or any of Your
                        confidential information.
                    </p>

                    <h4>10. Services Provided from United States.</h4>
                    <p>
                        Software is controlled and operated from facilities in the United
                        States. Those who access or use Software from other jurisdictions
                        do so at their own volition, expressly consent to the application
                        of United States law to all aspects of their access to and use of
                        Software, including with respect to the collection and use of
                        data, and are entirely responsible for compliance with all
                        applicable United States and local laws and regulations, including
                        export and import regulations.
                    </p>

                    <h4>11. Choice of Law and Choice of Venue.</h4>
                    <p>
                        This Agreement shall be governed by and construed in accordance
                        with the laws of the Commonwealth of Pennsylvania, without regard
                        to principles of conflicts of law. Any action, suit or proceeding
                        arising out of or related to Software or this Agreement shall be
                        brought only in the courts with proper subject matter jurisdiction
                        located in or having jurisdiction over the county in which KCF has
                        its principal place of business at the time of filing of such
                        action, suit or proceeding, and the parties hereby unconditionally
                        and irrevocably consent and submit to the exclusive jurisdiction
                        of such courts and waive any objection that they may now or
                        hereafter have with respect thereto. By using Software, you
                        irrevocably waive, to the fullest extent permitted by applicable
                        law, any and all right to trial by jury in any suit, action, or
                        proceeding arising out of or relating to this agreement or any
                        breach thereof.
                    </p>

                    <h4>
                        12. Incorporation by Reference of Additional Terms, Conditions,
                        Policies, and Statements
                    </h4>
                    <p>
                        By accepting these Software terms of use, Terms of Use, You accept
                        the following additional terms, conditions, policies, and
                        statements, which are incorporated by reference into the
                        Agreement:
                    </p>

                    <ul>
                        <li>
                            Privacy Policy (
                            <ExternalLink url="https://kcftech.com/privacy-policy/">
                                https://kcftech.com/privacy-policy/
                            </ExternalLink>
                            )
                        </li>
                        <li>
                            Terms of Sale (
                            <ExternalLink url="https://kcftech.com/terms-of-sale/">
                                https://kcftech.com/terms-of-sale/
                            </ExternalLink>
                            )
                        </li>
                    </ul>

                    <Buttons>
                        <Button
                            type="button"
                            appearance="primary"
                            onClick={() => acceptEula()}
                            disabled={isAcceptingEula}
                        >
                            Accept
                        </Button>
                        <Button
                            type="button"
                            appearance="secondary"
                            onClick={() => logout(true)}
                            disabled={isAcceptingEula}
                        >
                            Decline
                        </Button>
                    </Buttons>
                </TermsContainer>
                <div className="text-right">
                    <ExternalLink url={TermsOfUse}>
                        Download SMARTdiagnostics&copy; Terms of Use
                    </ExternalLink>
                </div>
            </LoginScreen>
        </>
    );
}
